import { useState, useEffect } from "react";
import { NavLink, Link } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { useLang } from "@/context/LanguageContext";

const Navbar = () => {
  const { t, lang, toggle } = useLang();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const links = [
    { to: "/", label: t.nav.home, id: "home", end: true },
    { to: "/trainer", label: t.nav.trainer, id: "trainer", lines: lang === "de" ? ["Unsere", "Trainer"] : ["Our", "Coaches"] },
    { to: "/kurse", label: t.nav.kurse, id: "kurse", lines: lang === "de" ? ["Gruppen", "kurse"] : ["Group", "courses"] },
    { to: "/einzeltraining", label: t.nav.einzeltraining, id: "einzeltraining", lines: lang === "de" ? ["Einzel", "training"] : ["Single", "lessons"] },
    { to: "/onlinecoaching", label: t.nav.onlinecoaching, id: "onlinecoaching", lines: lang === "de" ? ["Online", "coaching"] : ["Online", "coaching"] },
    { to: "/golfclub", label: t.nav.golfclub, id: "golfclub", lines: ["Golfclub", "GC KW"] },
    { to: "/trackman", label: lang === "de" ? "TrackMan buchen" : "Book TrackMan", id: "trackman", lines: lang === "de" ? ["TrackMan", "buchen"] : ["Book", "TrackMan"], highlight: true },
  ];

  return (
    <header
      data-testid="navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 border-b ${
        scrolled
          ? "bg-[#050505]/85 backdrop-blur-2xl border-white/10"
          : "bg-transparent border-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          <Link to="/" data-testid="logo-link" className="flex items-center group">
            <img
              src="/logo.png"
              alt="Golfcoaching Academy Wendlingen — powered by golfcoaching.online"
              className="h-11 md:h-14 w-auto"
            />
          </Link>

          {/* Desktop nav */}
          <nav className="hidden lg:flex items-center gap-1">
            {links.map((l) => (
              <NavLink
                key={l.id}
                to={l.to}
                end={l.end}
                data-testid={`nav-link-${l.id}`}
                className={({ isActive }) =>
                  `font-mono-lab text-xs uppercase tracking-widest px-3 py-2 transition-colors ${
                    l.lines ? "text-center leading-tight" : ""
                  } ${
                    isActive ? "text-[#ff0033]" : "text-neutral-300 hover:text-white"
                  }`
                }
              >
                {l.lines ? (
                  l.lines.map((line, i) => <span key={i} className="block">{line}</span>)
                ) : (
                  l.label
                )}
              </NavLink>
            ))}
          </nav>

          <div className="flex items-center gap-3">
            <button
              data-testid="language-switch-btn"
              onClick={toggle}
              className="font-mono-lab text-xs tracking-widest border border-white/20 px-2 py-1.5 sm:px-3 sm:py-2 hover:border-[#ff0033] hover:text-[#ff0033] transition-colors text-white flex items-center"
              aria-label={lang === "de" ? "Sprache auf Englisch wechseln" : "Switch language to German"}
              title={lang === "de" ? "Switch to English" : "Auf Deutsch wechseln"}
            >
              {/* Handy: Flagge der aktiven Sprache – Klick wechselt die Sprache */}
              <span className="sm:hidden flex items-center gap-1.5">
                {lang === "de" ? (
                  <svg width="22" height="15" viewBox="0 0 5 3" aria-hidden="true" className="border border-white/20">
                    <rect width="5" height="1" y="0" fill="#000" />
                    <rect width="5" height="1" y="1" fill="#DD0000" />
                    <rect width="5" height="1" y="2" fill="#FFCE00" />
                  </svg>
                ) : (
                  <svg width="22" height="15" viewBox="0 0 60 30" aria-hidden="true" className="border border-white/20">
                    <rect width="60" height="30" fill="#012169" />
                    <path d="M0,0 60,30 M60,0 0,30" stroke="#fff" strokeWidth="6" />
                    <path d="M0,0 60,30 M60,0 0,30" stroke="#C8102E" strokeWidth="3" />
                    <path d="M30,0 V30 M0,15 H60" stroke="#fff" strokeWidth="10" />
                    <path d="M30,0 V30 M0,15 H60" stroke="#C8102E" strokeWidth="5" />
                  </svg>
                )}
                <span className="text-[10px] text-[#ff0033]">{lang.toUpperCase()}</span>
              </span>
              {/* Desktop: Textumschalter */}
              <span className="hidden sm:inline">
                <span className={lang === "de" ? "text-[#ff0033]" : ""}>DE</span>
                <span className="text-neutral-600 mx-1">/</span>
                <span className={lang === "en" ? "text-[#ff0033]" : ""}>EN</span>
              </span>
            </button>

            <button
              data-testid="mobile-menu-toggle"
              onClick={() => setOpen((v) => !v)}
              className="lg:hidden text-white p-2"
              aria-label="Menu"
            >
              {open ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile nav */}
      {open && (
        <nav
          data-testid="mobile-nav"
          className="lg:hidden bg-[#050505] border-t border-white/10 px-4 py-6 flex flex-col gap-1"
        >
          {links.map((l) => (
            <NavLink
              key={l.id}
              to={l.to}
              end={l.end}
              onClick={() => setOpen(false)}
              data-testid={`mobile-nav-link-${l.id}`}
              className={({ isActive }) =>
                `font-mono-lab text-sm uppercase tracking-widest py-3 border-b border-white/5 ${
                  isActive ? "text-[#ff0033]" : "text-neutral-300"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
        </nav>
      )}
    </header>
  );
};

export default Navbar;
